import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, AreaPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { useCfmContext } from "../../../Context/CfmContext";
import { hexToRgba } from "./GetGradientColor";
import "../../../../../../css/CashflowModeller2/Charts/Graphs.css";
import { useRef } from "react";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import CustomChartWrapper from "../Utils/CustomWrapper";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";

/**
 * Renders a stacked pension graph visualizing pension fund values and cash flows over time for multiple pensions.
 *
 * The graph displays:
 * - A line and area plot for each pension's fund value over age.
 * - Stacked bar plots for each pension's income, TFC (tax-free cash), phased TFC, adhoc withdrawals, and adhoc contributions.
 * - Custom colors for each pension and flow type.
 * - Custom legends, axes, and tooltips.
 *
 * @function
 * @param {boolean} [isPrint=true] - If true, renders the chart with fixed width/height for printing; otherwise, uses responsive sizing.
 * @returns {JSX.Element} The rendered pension graph component.
 *
 * @example
 * <StackedPensionGraph isPrint={false} />
 *
 * @remarks
 * - Expects pension data from the CFM context (`state.pensionResults`).
 * - Uses ChartDataProviderPro and custom chart wrappers for rendering.
 * - Each pension's flows are stacked independently.
 */
const StackedPensionGraph = ({
  isPrint = false,
  animated = false,
  apiRef = null,
  idOverride,
}) => {
  const { state, dispatch } = useCfmContext();
  // const apiRef = useRef();
  const chartOnlyRef = useRef();

  // Colors per pension index for variety
  const colors = [
    {
      adhocContribution: "#FF5722",
      income: "#62B966",
      tfc: "#8DD3C7",
      phased: "#9C27B0",
      adhoc: "#E91E63",
      fund: "#57B5E7",
      regularIncome: "#6a1b9a",
    },
    // {
    //   fund: "#4A90E2",
    //   income: "#81C784",
    //   tfc: "#4DB6AC",
    //   phased: "#BA68C8",
    //   adhoc: "#F06292",
    //   adhocContribution: "#FF8A65",
    // },
    {
      fund: "#ff8a65",
      income: "#81C784",
      tfc: "#c792ea",
      phased: "#27cde2",
      adhoc: "#4db6ac",
      adhocContribution: "#FF8A65",
      regularIncome: "#8e24aa",
    },
    {
      fund: "#f57c00",
      income: "#2e7d32",
      tfc: "#1565c0",
      phased: "#6a1b9a",
      adhoc: "#8e24aa",
      adhocContribution: "#fdd835",
      regularIncome: "#E91E63",
    },
    {
      fund: "#ffa000",
      income: "#81c784",
      tfc: "#5e35b1",
      phased: "#ba68c8",
      adhoc: "#e1bee7",
      adhocContribution: "#fff176",
      regularIncome: "#57B5E7",
    },
  ];

  // Build all unique ages across all pensions
  const ageSet = new Set();
  (state.pensionResults || []).forEach((p) => {
    p.pensionFunds?.forEach((row) => {
      ageSet.add(row.age);
    });
  });
  const allAges = Array.from(ageSet).sort((a, b) => a - b);

  const parsePos = (v) => {
    const n = parseFloat((v || "0").replace(/[^0-9.-]+/g, ""));
    return n >= 0 ? n : 0;
  };

  // Build series: each pension gets its OWN line + bar stack
  const series = (state.pensionResults || []).flatMap((p, idx) => {
    const person = p.name;
    // console.log(`Building graph for ${person} - Pension ${idx + 1}`);
    const colorSet = colors[idx % colors.length];
    const label = `${p.name}: ${p.type || "Pension"} ${idx + 1}`;

    // Build age map for this pension
    const fundMap = new Map();
    p.pensionFunds?.forEach((row) => {
      fundMap.set(row.age, {
        fund: parsePos(row.fundValue),
        income: parsePos(row.taxedDrawdown),
        tfc: parsePos(row.tflsWithdrawal),
        phased: parsePos(row.phasedTfc),
        adhoc: parsePos(row.adhocWithdrawal),
        adhocContribution: parsePos(row.adhocContribution),
        regularIncome: parsePos(row.contribution),
      });
    });

    // This pension's Fund line + area
    const lineSeries = {
      type: "line",
      area: true,
      data: allAges.map((age) => fundMap.get(age)?.fund || 0),
      label: `${label} - Fund`,
      color: colorSet.fund,
    };

    // This pension's flows, stacked independently
    const barSeries = [
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.income || 0),
        label: `${label} - Income`,
        color: colorSet.income,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.tfc || 0),
        label: `${label} - TFC`,
        color: colorSet.tfc,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.phased || 0),
        label: `${label} - Phased TFC`,
        color: colorSet.phased,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhoc || 0),
        label: `${label} - Adhoc Withdrawal`,
        color: colorSet.adhoc,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhocContribution || 0),
        label: `${label} - Adhoc Contribution`,
        color: colorSet.adhocContribution,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.regularIncome || 0),
        label: `${label} - Contribution`,
        color: colorSet.regularIncome,
      },
    ];

    return [lineSeries, ...barSeries];
  });

  const visibleSeries = series.filter((s) =>
    s.data.some((v) => Number(v ?? 0) !== 0)
  );


  const graphHeight = useGraphHeight(isPrint);
  // console.log("Graph Height:", graphHeight, isPrint);

  return (
    <div
      ref={chartOnlyRef}
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "transparent",
      }}
      id={idOverride || "stacked-pen-graph"}
    >
      {/* <div className="cfm-graph-wrapper"> */}
      <ChartDataProviderPro
        apiRef={apiRef}
        // width={1200}
        
        height={graphHeight}
        fillMode="none"
        key="multi-pension-graph"
        series={visibleSeries}
        xAxis={[
          {
            id: "age-axis",
            scaleType: "band",
            data: allAges,
            label: "Age",
          },
        ]}
        yAxis={[
          {
            id: "value-axis",
            label: "Amount",
            valueFormatter: (val) => {
              if (val >= 1_000_000) return `£${(val / 1_000_000).toFixed(1)}m`;
              if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
              return `£${val}`;
            },
            width: 70,
          },
        ]}
      >
        <CustomChartWrapper isApiPrint={apiRef ? true : false}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            {/* Gradients for each line */}
            <defs>
              {series
                .filter((s) => s.type === "line")
                .map((s, idx) => (
                  <linearGradient
                    key={`grad-${idx}`}
                    id={`grad-${idx}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={hexToRgba(s.color, 0.2)} />
                    <stop offset="80%" stopColor={hexToRgba(s.color, 0)} />
                  </linearGradient>
                ))}
            </defs>

            {/* All pension bars */}
            <BarPlot springconfig={{ tension: 80, friction: 80 }} />

            {/* All pension fund areas */}
            {series
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <AreaPlot
                  key={`area-${idx}`}
                  seriesid={s.label}
                  springconfig={{ tension: 80, friction: 80 }}
                  slots={{
                    area: ({ d, style }) => (
                      <path
                        d={d}
                        style={style}
                        fill={`url(#grad-${idx})`}
                        stroke="none"
                      />
                    ),
                  }}
                />
              ))}

            {/* All pension fund lines */}
            {series
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <LinePlot
                  key={`line-${idx}`}
                  seriesid={s.label}
                  slotProps={{
                    line: {
                      style: {
                        strokeWidth: 4,
                      },
                    },
                  }}
                  springconfig={{ tension: 80, friction: 80 }}
                />
              ))}

            <ChartsXAxis
              axisId="age-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsYAxis
              axisId="value-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
    </div>
    // </div>
  );
};

export default StackedPensionGraph;
